import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Box, Button, IconButton, Typography } from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import AddIcon from "@mui/icons-material/Add";
import WaveSurfer from "wavesurfer.js";
import { API_BASE } from "config/api";
import { useTranslation } from "hooks/useTranslation";
import {
  MIN_SEGMENT_SECONDS,
  SEG_LIMIT,
  canAddSplitAt,
  clampSplitTickTime,
  findBestSplitPosition,
  fmtSeconds,
  getSegments,
  getSplitTickBounds,
  segDurationState,
} from "utils/segmentAnnotation";

const proxiedAudioUrl = (originalUrl) => {
  if (!originalUrl) return originalUrl;
  const url = new URL("api/audio/proxy", API_BASE);
  url.searchParams.set("url", originalUrl);
  return url.toString();
};

const SPEED_OPTIONS = [0.75, 1, 1.5, 2];

export default function SplitAnnotationPlayer({
  src,
  duration: durationProp,
  splitTimes,
  onSplitTimesChange,
  disabled,
}) {
  const { t } = useTranslation();
  const hiddenAudioRef = useRef(null);
  const waveContainerRef = useRef(null);
  const wsRef = useRef(null);
  const trackRef = useRef(null);
  const dragTimesRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [waveReady, setWaveReady] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(durationProp || 0);
  const [playSpeed, setPlaySpeed] = useState(1);
  const [draggingTickIndex, setDraggingTickIndex] = useState(null);

  useEffect(() => {
    if (durationProp > 0) setDuration(durationProp);
  }, [durationProp]);

  useEffect(() => {
    if (!waveContainerRef.current || !hiddenAudioRef.current) return undefined;

    setWaveReady(false);

    const ws = WaveSurfer.create({
      container: waveContainerRef.current,
      media: hiddenAudioRef.current,
      waveColor: "#bdbdbd",
      progressColor: "#1B4FD8",
      cursorColor: "#1B4FD8",
      height: 48,
      barWidth: 2,
      barGap: 1,
      barRadius: 1,
      normalize: true,
    });

    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onFinish = () => setIsPlaying(false);
    const onReady = () => {
      setWaveReady(true);
      const d = ws.getDuration() || durationProp || 0;
      if (d > 0) setDuration(d);
    };
    const onTimeUpdate = (t) => setCurrentTime(t || 0);

    ws.on("play", onPlay);
    ws.on("pause", onPause);
    ws.on("finish", onFinish);
    ws.on("ready", onReady);
    ws.on("timeupdate", onTimeUpdate);

    wsRef.current = ws;

    return () => {
      wsRef.current = null;
      setWaveReady(false);
      setIsPlaying(false);
      ws.destroy();
    };
  }, [src, durationProp]);

  useEffect(() => {
    if (hiddenAudioRef.current) {
      hiddenAudioRef.current.playbackRate = playSpeed;
    }
  }, [playSpeed]);

  const sortedSplitTimes = useMemo(
    () => [...(splitTimes || [])].sort((a, b) => a - b),
    [splitTimes]
  );

  const segments = useMemo(
    () => getSegments(sortedSplitTimes, duration),
    [sortedSplitTimes, duration]
  );

  const togglePlay = () => {
    if (!wsRef.current || disabled) return;
    if (wsRef.current.isPlaying()) wsRef.current.pause();
    else wsRef.current.play();
  };

  const seekTo = useCallback(
    (pct) => {
      if (!wsRef.current || duration <= 0) return;
      const t = Math.max(0, Math.min(duration, pct * duration));
      wsRef.current.setTime(t);
      setCurrentTime(t);
    },
    [duration]
  );

  const handleTrackClick = (e) => {
    if (disabled || !trackRef.current) return;
    if (e.target.closest("[data-split-tick]")) return;
    const rect = trackRef.current.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    seekTo(pct);
  };

  const addSplitAtPlayhead = () => {
    if (disabled || duration <= 0) return;
    let splitTime = currentTime;

    if (!canAddSplitAt(splitTime, sortedSplitTimes, duration)) {
      const fallback = findBestSplitPosition(sortedSplitTimes, duration);
      if (fallback == null) return;
      splitTime = fallback;
    }

    onSplitTimesChange([...sortedSplitTimes, splitTime].sort((a, b) => a - b));
  };

  const addSplitAtSegmentClick = (e, seg) => {
    if (disabled) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    const splitTime = seg.start + pct * (seg.end - seg.start);
    if (!canAddSplitAt(splitTime, sortedSplitTimes, duration)) return;
    onSplitTimesChange([...sortedSplitTimes, splitTime].sort((a, b) => a - b));
  };

  const removeSplit = (tickIndex) => {
    if (disabled) return;
    const next = sortedSplitTimes.filter((_, i) => i !== tickIndex);
    onSplitTimesChange(next);
  };

  const applyTickTimeFromPointer = useCallback(
    (tickIndex, clientX, baseTimes) => {
      if (!trackRef.current || duration <= 0) return baseTimes;
      const rect = trackRef.current.getBoundingClientRect();
      const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      const rawTime = pct * duration;
      const clamped = clampSplitTickTime(rawTime, tickIndex, baseTimes, duration);
      const next = [...baseTimes];
      next[tickIndex] = clamped;
      return next;
    },
    [duration]
  );

  const startDragTick = (e, tickIndex) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();

    const initialTimes = [...sortedSplitTimes];
    dragTimesRef.current = initialTimes;

    const move = (ev) => {
      const next = applyTickTimeFromPointer(tickIndex, ev.clientX, dragTimesRef.current);
      dragTimesRef.current = next;
      onSplitTimesChange(next);
    };

    const up = () => {
      document.removeEventListener("mousemove", move);
      document.removeEventListener("mouseup", up);
      const finalTimes = [...(dragTimesRef.current || sortedSplitTimes)].sort((a, b) => a - b);
      dragTimesRef.current = null;
      setDraggingTickIndex(null);
      onSplitTimesChange(finalTimes);
    };

    setDraggingTickIndex(tickIndex);
    document.addEventListener("mousemove", move);
    document.addEventListener("mouseup", up);
  };

  const canSplitAtPlayhead = canAddSplitAt(currentTime, sortedSplitTimes, duration);
  const fallbackPos = canSplitAtPlayhead ? null : findBestSplitPosition(sortedSplitTimes, duration);
  const canAddSplit = canSplitAtPlayhead || fallbackPos != null;

  const addSplitTooltip = canSplitAtPlayhead
    ? t("annotator.annotationPage.splitPlayer.addSplitAtPlayhead", "Split at playhead position")
    : fallbackPos != null
    ? t("annotator.annotationPage.splitPlayer.addSplitLongest", "Split longest segment at midpoint")
    : t("annotator.annotationPage.splitPlayer.splitDisabled", "No segment can be split further");

  const dragPreview =
    draggingTickIndex != null && sortedSplitTimes[draggingTickIndex] != null
      ? (() => {
          const t = sortedSplitTimes[draggingTickIndex];
          const prevStart = draggingTickIndex === 0 ? 0 : sortedSplitTimes[draggingTickIndex - 1];
          const nextEnd =
            draggingTickIndex === sortedSplitTimes.length - 1
              ? duration
              : sortedSplitTimes[draggingTickIndex + 1];
          return {
            leftDur: t - prevStart,
            rightDur: nextEnd - t,
            time: t,
          };
        })()
      : null;

  const pct = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
        <IconButton
          onClick={togglePlay}
          disabled={disabled || !waveReady}
          sx={{
            width: 34,
            height: 34,
            bgcolor: "#1A1917",
            color: "#fff",
            "&:hover": { bgcolor: "#333" },
            "&.Mui-disabled": { bgcolor: "#ccc", color: "#fff" },
          }}
        >
          {isPlaying ? (
            <PauseIcon sx={{ fontSize: 18 }} />
          ) : (
            <PlayArrowIcon sx={{ fontSize: 18 }} />
          )}
        </IconButton>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            ref={waveContainerRef}
            sx={{ width: "100%", minHeight: 48, bgcolor: "#fafafa", borderRadius: 1, mb: 0.5 }}
          />

          <Box
            sx={{
              display: "flex",
              gap: 0.25,
              height: 20,
              mb: 0.5,
              borderRadius: 0.5,
              overflow: "visible",
              position: "relative",
            }}
          >
            {segments.map((seg, i) => {
              const dur = seg.end - seg.start;
              const st = segDurationState(dur);
              const bg = st === "over" ? "#FEF2F2" : st === "warn" ? "#FFFBEB" : "#F0EFEB";
              const border = st === "over" ? "#FCA5A5" : st === "warn" ? "#FCD34D" : "#E2E0DA";
              const color = st === "over" ? "#DC2626" : st === "warn" ? "#B45309" : "#6B6860";
              const isDraggingAdjacent = draggingTickIndex === i || draggingTickIndex === i - 1;
              const canSplitSegment = dur >= MIN_SEGMENT_SECONDS * 2;
              return (
                <Box
                  key={i}
                  title={t(
                    "annotator.annotationPage.splitPlayer.segmentTooltip",
                    "Segment {{n}}: {{start}}–{{end}} ({{dur}})",
                    {
                      n: i + 1,
                      start: fmtSeconds(seg.start),
                      end: fmtSeconds(seg.end),
                      dur: fmtSeconds(dur),
                    }
                  )}
                  onDoubleClick={(e) => addSplitAtSegmentClick(e, seg)}
                  sx={{
                    flex: dur,
                    minWidth: 0,
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.5,
                    fontFamily: "monospace",
                    fontSize: "10px",
                    fontWeight: 500,
                    bgcolor: isDraggingAdjacent ? "#EBF0FF" : bg,
                    border: `1px solid ${isDraggingAdjacent ? "#1B4FD8" : border}`,
                    borderRadius: 0.5,
                    color,
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    transition: "background-color 0.1s, border-color 0.1s",
                    cursor: disabled || !canSplitSegment ? "default" : "crosshair",
                  }}
                >
                  {t("annotator.annotationPage.splitPlayer.segLabel", "Seg {{n}}", { n: i + 1 })}
                  <Box component="span" sx={{ opacity: 0.8 }}>
                    {fmtSeconds(dur)}
                  </Box>
                  {i < segments.length - 1 && (
                    <Box
                      data-split-tick="1"
                      onMouseDown={(e) => startDragTick(e, i)}
                      title={t(
                        "annotator.annotationPage.splitPlayer.dragResize",
                        "Drag to resize segment"
                      )}
                      sx={{
                        position: "absolute",
                        right: -4,
                        top: -2,
                        bottom: -2,
                        width: 8,
                        cursor: disabled ? "default" : "ew-resize",
                        zIndex: 2,
                        "&:hover": {
                          bgcolor: "rgba(220,38,38,0.15)",
                        },
                      }}
                    />
                  )}
                </Box>
              );
            })}
          </Box>

          {dragPreview && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: 2,
                mb: 0.5,
                py: 0.25,
                px: 1,
                bgcolor: "#EBF0FF",
                borderRadius: 0.5,
                border: "1px solid #93B4FF",
              }}
            >
              <Typography
                variant="caption"
                sx={{ fontFamily: "monospace", color: "#1B4FD8", fontWeight: 600 }}
              >
                ← {fmtSeconds(dragPreview.leftDur)}
              </Typography>
              <Typography variant="caption" sx={{ fontFamily: "monospace", color: "#6B6860" }}>
                @ {fmtSeconds(dragPreview.time)}
              </Typography>
              <Typography
                variant="caption"
                sx={{ fontFamily: "monospace", color: "#1B4FD8", fontWeight: 600 }}
              >
                {fmtSeconds(dragPreview.rightDur)} →
              </Typography>
            </Box>
          )}

          <Box sx={{ position: "relative", py: 1.5, pb: 2.5 }}>
            <Box
              ref={trackRef}
              onClick={handleTrackClick}
              sx={{
                height: 4,
                bgcolor: "#E2E0DA",
                borderRadius: 0.5,
                cursor: disabled ? "default" : "pointer",
                position: "relative",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  height: "100%",
                  width: `${pct}%`,
                  bgcolor: "#1B4FD8",
                  borderRadius: 0.5,
                  pointerEvents: "none",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: `${pct}%`,
                  width: 11,
                  height: 11,
                  bgcolor: "#1B4FD8",
                  borderRadius: "50%",
                  transform: "translate(-50%, -50%)",
                  pointerEvents: "none",
                  zIndex: 1,
                }}
              />
              {sortedSplitTimes.map((splitTime, i) => {
                const { minTime, maxTime } = getSplitTickBounds(i, sortedSplitTimes, duration);
                const atMin = Math.abs(splitTime - minTime) < 0.05;
                const atMax = Math.abs(splitTime - maxTime) < 0.05;
                return (
                  <Box
                    key={`${i}-${splitTime.toFixed(3)}`}
                    data-split-tick="1"
                    onContextMenu={(e) => {
                      e.preventDefault();
                      removeSplit(i);
                    }}
                    onMouseDown={(e) => startDragTick(e, i)}
                    title={`${t(
                      "annotator.annotationPage.splitPlayer.dragResizeTick",
                      "Drag to resize · max 2:00 per segment · right-click to remove"
                    )}${
                      atMin || atMax
                        ? t("annotator.annotationPage.splitPlayer.atLimit", " · at limit")
                        : ""
                    }`}
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: `${duration > 0 ? (splitTime / duration) * 100 : 0}%`,
                      width: draggingTickIndex === i ? 5 : 4,
                      height: 36,
                      bgcolor: draggingTickIndex === i ? "#B91C1C" : "#DC2626",
                      borderRadius: 0.5,
                      transform: "translate(-50%, -50%)",
                      cursor: disabled ? "default" : "ew-resize",
                      zIndex: draggingTickIndex === i ? 4 : 3,
                      boxShadow:
                        draggingTickIndex === i
                          ? "0 2px 8px rgba(220,38,38,0.45)"
                          : "0 1px 3px rgba(220,38,38,0.3)",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: -3,
                        left: "50%",
                        width: draggingTickIndex === i ? 15 : 13,
                        height: draggingTickIndex === i ? 15 : 13,
                        borderRadius: "50%",
                        bgcolor: "#DC2626",
                        border: "2px solid #fff",
                        transform: "translateX(-50%)",
                      },
                      "&:hover": { width: 5, bgcolor: "#B91C1C" },
                    }}
                  />
                );
              })}
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
              <Typography
                variant="caption"
                sx={{ fontFamily: "monospace", color: "#6B6860", fontWeight: 500 }}
              >
                {fmtSeconds(currentTime)}
              </Typography>
              <Typography variant="caption" sx={{ fontFamily: "monospace", color: "#A09D97" }}>
                {fmtSeconds(duration)}
              </Typography>
            </Box>
          </Box>
        </Box>

        {duration > SEG_LIMIT && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon sx={{ fontSize: 14 }} />}
            onClick={addSplitAtPlayhead}
            disabled={disabled || !canAddSplit}
            title={addSplitTooltip}
            sx={{
              flexShrink: 0,
              fontFamily: "monospace",
              fontSize: "11px",
              borderStyle: "dashed",
              color: canAddSplit ? "#6B6860" : "#A09D97",
              borderColor: "#C8C5BC",
              whiteSpace: "nowrap",
              "&:hover": {
                borderStyle: "solid",
                borderColor: "#1B4FD8",
                color: "#1B4FD8",
                bgcolor: "#EBF0FF",
              },
            }}
          >
            {t("annotator.annotationPage.splitPlayer.addSplit", "Add split")}
          </Button>
        )}

        <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
          {SPEED_OPTIONS.map((speed) => (
            <Button
              key={speed}
              size="small"
              onClick={() => setPlaySpeed(speed)}
              disabled={disabled}
              sx={{
                minWidth: 36,
                px: 1,
                fontFamily: "monospace",
                fontSize: "11px",
                border: "1px solid",
                borderColor: playSpeed === speed ? "#1B4FD8" : "#E2E0DA",
                color: playSpeed === speed ? "#1B4FD8" : "#6B6860",
                bgcolor: playSpeed === speed ? "#EBF0FF" : "transparent",
              }}
            >
              {speed}x
            </Button>
          ))}
        </Box>
      </Box>

      <audio
        ref={hiddenAudioRef}
        src={proxiedAudioUrl(src)}
        crossOrigin="anonymous"
        preload="auto"
        style={{ display: "none" }}
      />
    </Box>
  );
}

SplitAnnotationPlayer.propTypes = {
  src: PropTypes.string.isRequired,
  duration: PropTypes.number,
  splitTimes: PropTypes.arrayOf(PropTypes.number).isRequired,
  onSplitTimesChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

SplitAnnotationPlayer.defaultProps = {
  duration: 0,
  disabled: false,
};
